/**
 * Threat Intelligence Hooks
 * 
 * Custom React hooks for consuming ChatGPT-powered threat intelligence.
 * Provides real-time data fetching with automatic refresh, caching, and error handling.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  DashboardThreatOverview,
  CategoryThreatData,
  ThreatSummaryResponse,
  CategoryComparisonResponse,
  ThreatCategoryKey,
  VulnerableCompaniesReport
} from "../../types/threatIntelligence";
import {
  getThreatInsights,
  getCategoryThreatInsights,
  getThreatSummary,
  getCategoryComparison,
  refreshThreatInsights,
  getVulnerableCompaniesReport
} from "./threatIntelligenceApi";

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes client-side cache
const AUTO_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes auto-refresh

// Simple in-memory cache
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const cache = new Map<string, CacheEntry<any>>();

function getCached<T>(key: string): T | null {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) {
    return entry.data as T;
  }
  cache.delete(key);
  return null;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

// Generic hook state interface
interface UseQueryState<T> {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching comprehensive threat intelligence overview
 * Includes automatic refresh and caching
 */
export function useThreatInsights(options?: {
  autoRefresh?: boolean;
  refreshInterval?: number;
}): UseQueryState<DashboardThreatOverview> {
  const { autoRefresh = true, refreshInterval = AUTO_REFRESH_INTERVAL } = options || {};
  
  const [data, setData] = useState<DashboardThreatOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (skipCache = false) => {
    const cacheKey = "threat-insights";
    
    // Check cache first (unless skipping)
    if (!skipCache) {
      const cached = getCached<DashboardThreatOverview>(cacheKey);
      if (cached) {
        setData(cached);
        setIsLoading(false);
        setLastUpdated(new Date(cached.generatedAt));
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await getThreatInsights();
      
      if (mountedRef.current) {
        setData(result);
        setCache(cacheKey, result);
        setLastUpdated(new Date(result.generatedAt));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error("Failed to fetch threat insights"));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    
    const intervalId = setInterval(() => {
      fetchData(true); // Skip cache for refresh
    }, refreshInterval);

    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, fetchData]);

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, isLoading, error, lastUpdated, refetch };
}

/**
 * Hook for fetching category-specific threat insights
 */
export function useCategoryInsights(
  category: ThreatCategoryKey,
  options?: { autoRefresh?: boolean }
): UseQueryState<CategoryThreatData> {
  const { autoRefresh = false } = options || {};
  
  const [data, setData] = useState<CategoryThreatData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (skipCache = false) => {
    const cacheKey = `category-insights-${category}`;
    
    if (!skipCache) {
      const cached = getCached<CategoryThreatData>(cacheKey);
      if (cached) {
        setData(cached);
        setIsLoading(false);
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await getCategoryThreatInsights(category);
      
      if (mountedRef.current) {
        setData(result);
        setCache(cacheKey, result);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error(`Failed to fetch ${category} insights`));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [category]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalId = setInterval(() => fetchData(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [autoRefresh, fetchData]);

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, isLoading, error, lastUpdated, refetch };
}

/**
 * Hook for fetching lightweight threat summary
 */
export function useThreatSummary(options?: {
  autoRefresh?: boolean;
}): UseQueryState<ThreatSummaryResponse> {
  const { autoRefresh = true } = options || {};
  
  const [data, setData] = useState<ThreatSummaryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (skipCache = false) => {
    const cacheKey = "threat-summary";
    
    if (!skipCache) {
      const cached = getCached<ThreatSummaryResponse>(cacheKey);
      if (cached) {
        setData(cached);
        setIsLoading(false);
        setLastUpdated(new Date(cached.generatedAt));
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await getThreatSummary();
      
      if (mountedRef.current) {
        setData(result);
        setCache(cacheKey, result);
        setLastUpdated(new Date(result.generatedAt));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error("Failed to fetch threat summary"));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalId = setInterval(() => fetchData(true), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [autoRefresh, fetchData]);

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, isLoading, error, lastUpdated, refetch };
}

/**
 * Hook for fetching vulnerable companies report
 */
export function useVulnerableCompaniesReport(options?: {
  autoRefresh?: boolean;
  refreshInterval?: number;
}): UseQueryState<VulnerableCompaniesReport> {
  const { autoRefresh = true, refreshInterval = AUTO_REFRESH_INTERVAL } = options || {};

  const [data, setData] = useState<VulnerableCompaniesReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (skipCache = false) => {
    const cacheKey = "vulnerable-companies-report";

    if (!skipCache) {
      const cached = getCached<VulnerableCompaniesReport>(cacheKey);
      if (cached) {
        setData(cached);
        setIsLoading(false);
        setLastUpdated(new Date(cached.generatedAt));
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await getVulnerableCompaniesReport();

      if (mountedRef.current) {
        setData(result);
        setCache(cacheKey, result);
        setLastUpdated(new Date(result.generatedAt));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error("Failed to fetch vulnerable companies report"));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const intervalId = setInterval(() => fetchData(true), refreshInterval);
    return () => clearInterval(intervalId);
  }, [autoRefresh, refreshInterval, fetchData]);

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, isLoading, error, lastUpdated, refetch };
}

/**
 * Hook for fetching category comparison data
 */
export function useCategoryComparison(): UseQueryState<CategoryComparisonResponse> {
  const [data, setData] = useState<CategoryComparisonResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (skipCache = false) => {
    const cacheKey = "category-comparison";
    
    if (!skipCache) {
      const cached = getCached<CategoryComparisonResponse>(cacheKey);
      if (cached) {
        setData(cached);
        setIsLoading(false);
        setLastUpdated(new Date(cached.generatedAt));
        return;
      }
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await getCategoryComparison();
      
      if (mountedRef.current) {
        setData(result);
        setCache(cacheKey, result);
        setLastUpdated(new Date(result.generatedAt));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err : new Error("Failed to fetch category comparison"));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  const refetch = useCallback(async () => {
    await fetchData(true);
  }, [fetchData]);

  return { data, isLoading, error, lastUpdated, refetch };
}

/**
 * Hook for manually refreshing threat intelligence cache
 */
export function useRefreshInsights() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setError(null);
      
      // Clear client-side cache
      cache.clear();
      
      // Trigger server-side refresh
      await refreshThreatInsights();
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to refresh insights"));
      throw err;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  return { refresh, isRefreshing, error };
}

/**
 * Hook to clear all cached threat data
 */
export function useClearCache() {
  return useCallback(() => {
    cache.clear();
  }, []);
}
